import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import * as mutations from '../graphql/mutations';
import * as queries from '../graphql/queries';

const client = generateClient();

/**
 * Saves category points for a user
 * @param {string} trainingPath - The training path (IMT, CST, ACCS)
 * @param {Array} categories - Array of category point objects
 * @returns {Promise} - The GraphQL mutation result
 */
export const saveCategoryPoints = async (trainingPath, categories) => {
  try {
    // Get current user
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    console.log(`Saving category points for ${trainingPath}:`, categories);
    
    // First check if there's an existing record for this user and training path
    const { data: existingData } = await client.graphql({
      query: queries.listCategoryPointsRecords,
      variables: {
        filter: { 
          userId: { eq: user.username }, 
          trainingPath: { eq: trainingPath }
        }
      },
      authMode: 'userPool'
    });
    
    const existingRecord = existingData.listCategoryPointsRecords?.items?.[0];
    
    // Convert categories array to stringified JSON for storage
    const categoriesData = JSON.stringify(categories);
    
    if (existingRecord) {
      // Update existing record
      const input = {
        id: existingRecord.id,
        userId: user.username,
        trainingPath,
        categoriesData,
      };
      
      const result = await client.graphql({
        query: mutations.updateCategoryPointsRecord,
        variables: { input },
        authMode: 'userPool'
      });
      
      console.log(`Updated category points for ${trainingPath}:`, result);
      return result;
    } else {
      // Create new record
      const input = {
        userId: user.username,
        trainingPath,
        categoriesData,
      };
      
      const result = await client.graphql({
        query: mutations.createCategoryPointsRecord,
        variables: { input },
        authMode: 'userPool'
      });
      
      console.log(`Created category points for ${trainingPath}:`, result);
      return result;
    }
  } catch (error) {
    console.error('Error saving category points:', error);
    throw error;
  }
};

/**
 * Retrieves category points for a user and training path
 * @param {string} trainingPath - The training path to retrieve points for
 * @returns {Promise<Array>} - Array of category point objects or null if not found
 */
export const getCategoryPoints = async (trainingPath) => {
  try {
    // Get current user
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const { data } = await client.graphql({
      query: queries.listCategoryPointsRecords,
      variables: {
        filter: { 
          userId: { eq: user.username }, 
          trainingPath: { eq: trainingPath }
        }
      },
      authMode: 'userPool'
    });
    
    const record = data.listCategoryPointsRecords?.items?.[0];
    
    if (record && record.categoriesData) {
      try {
        // Parse the JSON string back to an array
        return JSON.parse(record.categoriesData);
      } catch (e) {
        console.error('Error parsing categories data:', e);
        return null;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error retrieving category points:', error);
    return null;
  }
};
